import React, { useState, useEffect, useContext } from 'react';
import ResearchLayout from '../../components/ResearchLayout';
import { protocolService } from '../../services/protocolService';
import { questionService } from '../../services/questionService';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const MedicalAdminProtocols = () => {
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [protocolsResponse, questionsResponse] = await Promise.all([
        protocolService.getProtocols(),
        questionService.getQuestions()
      ]);
      // Log the full response for debugging
      console.log('Full protocolsResponse:', protocolsResponse);

      // Defensive extraction
      const protocolsArr =
        (protocolsResponse.data && protocolsResponse.data.protocols) ||
        protocolsResponse.protocols ||
        [];
      setProtocols(protocolsArr);
      console.log('Protocols received:', protocolsArr);

      setQuestions(
        (questionsResponse.data && questionsResponse.data.questions) ||
        questionsResponse.data ||
        questionsResponse.questions ||
        []
      );
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load protocols and questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getProtocolQuestions = (protocol) => {
    // Use the questions count from backend
    return protocol.questions_count || 0;
  };

  const getProtocolAssignedDoctors = (protocol) => {
    // Use the assigned doctors details from backend
    return protocol.assigned_doctors_details || [];
  };

  const getProtocolStats = (protocol) => {
    // Use the data from backend if available, otherwise fallback to 0
    const totalCases = protocol.total_cases_assigned || 0;
    const assignedDoctors = protocol.assigned_doctors_count || 0;
    return { totalCases, assignedDoctors };
  };

  const calculateOverallStats = () => {
    let totalActiveAssignments = 0;
    let uniqueAssignedDoctors = new Set();

    protocols.forEach(protocol => {
      // Count total active assignments (total cases assigned)
      totalActiveAssignments += protocol.total_cases_assigned || 0;

      // Count unique doctors across all protocols
      if (protocol.assigned_doctors_details) {
        protocol.assigned_doctors_details.forEach(doctor => {
          uniqueAssignedDoctors.add(doctor.id);
        });
      }
    });

    return {
      totalActiveAssignments,
      totalAvailableDoctors: uniqueAssignedDoctors.size
    };
  };


  const overallStats = calculateOverallStats();

  if (loading) {
    return (
      <ResearchLayout pageTitle="View Protocols">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading protocols...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (error) {
    return (
      <ResearchLayout pageTitle="View Protocols">
        <div className="container-fluid">
          <div className="alert alert-danger" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button
              className="btn btn-outline-danger btn-sm ms-2"
              onClick={loadData}
            >
              Try Again
            </button>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="View Protocols">
      <div className="container-fluid">
        {/* Success Alert */}
        {success && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {success}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccess('')}
            ></button>
          </div>
        )}

        {/* Protocols Overview */}
        <div className="row mb-4">
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm bg-primary text-white">
              <div className="card-body text-center">
                <i className="fas fa-clipboard-list fa-2x mb-2"></i>
                <h4>{protocols.length}</h4>
                <p className="mb-0">Total Protocols</p>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm bg-success text-white">
              <div className="card-body text-center">
                <i className="fas fa-question-circle fa-2x mb-2"></i>
                <h4>{questions.length}</h4>
                <p className="mb-0">Total Questions</p>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm bg-info text-white">
              <div className="card-body text-center">
                <i className="fas fa-tasks fa-2x mb-2"></i>
                <h4>{overallStats.totalActiveAssignments}</h4>
                <p className="mb-0">Active Assignments</p>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm bg-warning text-white">
              <div className="card-body text-center">
                <i className="fas fa-user-md fa-2x mb-2"></i>
                <h4>{overallStats.totalAvailableDoctors}</h4>
                <p className="mb-0">Available Doctors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Protocols List */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Available Research Protocols
                </h5>
              </div>
              <div className="card-body">
                {protocols.map((protocol) => {
                  const questionsCount = getProtocolQuestions(protocol);
                  const assignedDoctors = getProtocolAssignedDoctors(protocol);
                  const stats = getProtocolStats(protocol);

                  return (
                    <div key={protocol.id} className="card mb-3 border">
                      <div className="card-header bg-light">
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <h6 className="mb-1 text-primary fw-bold">
                              <i className="fas fa-clipboard-list me-2"></i>
                              {protocol.name}
                              <span className={`badge ms-2 ${protocol.show_ai_response ? 'bg-info' : 'bg-secondary'}`}
                                title={protocol.show_ai_response ? 'Doctors can view AI assessment' : 'Doctors cannot view AI assessment'}>
                                <i className="fas fa-robot me-1"></i>
                                {protocol.show_ai_response ? 'AI Viewed' : 'AI Hidden'}
                              </span>
                            </h6>
                            <small className="text-muted">
                              Created by: {protocol.created_by_name || 'Unknown'} |
                              {new Date(protocol.created_at).toLocaleDateString()}
                            </small>
                          </div>
                          <div className="col-md-4 text-end">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setSelectedProtocol(
                                selectedProtocol === protocol.id ? null : protocol.id
                              )}
                            >
                              <i className="fas fa-eye me-1"></i>
                              {selectedProtocol === protocol.id ? 'Hide Details' : 'View Details'}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="card-body">
                        <p className="text-muted mb-3">{protocol.description}</p>
                        
                        {/* Protocol Stats */}
                        <div className="row mb-3">
                          <div className="col-md-3">
                            <div className="text-center">
                              <div className="h5 text-info mb-1">{questionsCount}</div>
                              <div className="small text-muted">Questions</div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-center">
                              <div className="h5 text-success mb-1">{stats.assignedDoctors}</div>
                              <div className="small text-muted">Assigned Doctors</div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-center">
                              <div className="h5 text-warning mb-1">{stats.totalCases}</div>
                              <div className="small text-muted">Total Cases Assigned To Doctors</div>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details - Assigned Doctors Only */}
                        {selectedProtocol === protocol.id && (
                          <div className="border-top pt-3">
                            <div className="row">
                              {/* Assigned Doctors */}
                              <div className="col-12">
                                <h6 className="text-success mb-3">
                                  <i className="fas fa-user-md me-2"></i>
                                  Assigned Doctors ({assignedDoctors.length})
                                </h6>
                                {assignedDoctors.length > 0 ? (
                                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {assignedDoctors.map((doctor) => (
                                      <div key={doctor.id} className="card mb-2 border-0 bg-light">
                                        <div className="card-body p-3">
                                          <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                              <div className="fw-bold text-success">
                                                <i className="fas fa-user-md me-2"></i>
                                                Dr. {doctor.name}
                                              </div>
                                              <div className="small text-muted">
                                                <i className="fas fa-envelope me-1"></i>
                                                {doctor.email}
                                              </div>
                                            </div>
                                            <span className="badge bg-primary">
                                              <i className="fas fa-briefcase-medical me-1"></i>
                                              {doctor.cases_count} cases
                                            </span>
                                          </div>
                                          <div className="mt-2">
                                            <span className="badge bg-success">
                                              <i className="fas fa-check me-1"></i>
                                              Active
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-4 text-muted">
                                    <i className="fas fa-user-md fa-3x mb-3"></i>
                                    <h6>No Doctors Assigned</h6>
                                    <p className="small">This protocol has not been assigned to any doctors yet.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="card-footer bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="small text-muted">
                            Last updated: {protocol.updated_at ? new Date(protocol.updated_at).toLocaleDateString() : 'N/A'}
                          </div>
                          <div>
                            <span className="badge bg-success me-2">
                              <i className="fas fa-check me-1"></i>
                              Active
                            </span>
                            {questionsCount > 0 && (
                              <span className="badge bg-info">
                                <i className="fas fa-link me-1"></i>
                                Questions Linked
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {protocols.length === 0 && (
                  <div className="text-center py-5">
                    <i className="fas fa-clipboard-list fa-4x text-muted mb-4"></i>
                    <h5 className="text-muted">No Protocols Available</h5>
                    <p className="text-muted">
                      Protocols need to be created by Program Admins before they can be assigned.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Protocol Usage Analytics - Commented Out */}
        {/*
        {protocols.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light">
                  <h6 className="mb-0">
                    <i className="fas fa-chart-bar me-2 text-primary"></i>
                    Protocol Usage Analytics
                  </h6>
                </div>
                <div className="card-body">
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Protocol</th>
                          <th>Questions</th>
                          <th>Assignments</th>
                          <th>Total Cases</th>
                          <th>Usage Rate</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {protocols.map(protocol => {
                          const protocolQuestions = getProtocolQuestions(protocol.id);
                          const stats = getProtocolStats(protocol.id);
                          const usageRate = 0;

                          return (
                            <tr key={protocol.id}>
                              <td>
                                <div className="fw-bold">{protocol.name}</div>
                                <div className="small text-muted">
                                  {protocol.description.substring(0, 50)}...
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-info">{protocolQuestions.length}</span>
                              </td>
                              <td>
                                <span className="badge bg-success">{stats.assignedDoctors}</span>
                              </td>
                              <td>
                                <span className="badge bg-warning">{stats.totalCases}</span>
                              </td>
                              <td>
                                <div className="progress" style={{ width: '100px', height: '6px' }}>
                                  <div
                                    className="progress-bar bg-primary"
                                    style={{ width: `${usageRate}%` }}
                                  ></div>
                                </div>
                                <small className="text-muted">{usageRate}%</small>
                              </td>
                              <td>
                                <span className="badge bg-success">Active</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="d-block d-md-none">
                    {protocols.map(protocol => {
                      const protocolQuestions = getProtocolQuestions(protocol.id);
                      const stats = getProtocolStats(protocol.id);
                      const usageRate = 0;

                      return (
                        <div key={protocol.id} className="card mb-3 border border-primary">
                          <div className="card-body p-3">
                            <div className="fw-bold mb-1">{protocol.name}</div>
                            <div className="small text-muted mb-2">
                              {protocol.description.substring(0, 50)}...
                            </div>
                            <div className="mb-2">
                              <span className="badge bg-info me-1">
                                {protocolQuestions.length} Questions
                              </span>
                              <span className="badge bg-success me-1">
                                {stats.assignedDoctors} Assignments
                              </span>
                              <span className="badge bg-warning me-1">
                                {stats.totalCases} Cases
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Usage Rate:</strong>
                              <div className="progress my-1" style={{ height: '6px' }}>
                                <div
                                  className="progress-bar bg-primary"
                                  style={{ width: `${usageRate}%` }}
                                ></div>
                              </div>
                              <small className="text-muted">{usageRate}%</small>
                            </div>
                            <div>
                              <span className="badge bg-success">Active</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        */}
      </div>
    </ResearchLayout>
  );
};

export default MedicalAdminProtocols;